<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import TheFileSystem from '../../components/TheFileSystem.vue'
import { useOtherEntityStore } from '@/modules/investing/stores/other-entity-store'
import OtherEntityNav from '@/modules/investing/components/other-entity-nav.vue'
import OtherEntityHeader from '@/modules/investing/components/other-entity-header.vue'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import { useCommitmentStore } from '@/modules/investing/stores/commitment-store'
import { VButton, VButtonGroup } from '@/modules/shared/components'
import { useFilter } from '@/modules/core/composables/useFilter'
import { createQueryString } from '@/modules/shared/utils/routing'
import { useFilingStore } from '../../stores/filing-store'

const { other_entity_id } = useRoute().params as { other_entity_id: string; slug: string }
const { tab: queryTab } = useRoute().query as { tab: string }

const commitmentStore = useCommitmentStore()
const filingStore = useFilingStore()
const otherEntityStore = useOtherEntityStore()

const theFileSystem = ref(null)

const cid = computed(() => `funding-entity:${other_entity_id}`)
const otherEntity = computed(() => otherEntityStore.items.get(cid.value))
const shareholders = computed(() => commitmentStore.commitments.map((commitment) => commitment.investor))
const rootFolder = computed(() =>
  filingStore.folders.find((folder) => folder.id === otherEntity.value?.filing_folder_root_id),
)

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()
const { filters, setFilter } = useFilter({
  tab: queryTab || 'shared', // 'direct', 'shared'
})

const updateURLQuery = () => {
  const queryString = createQueryString(filters.value)
  window.history.replaceState(null, null, `?${queryString}`)
}

const selectTab = (tab: string) => {
  setFilter('tab', tab)
  updateURLQuery()
}

const fetchAll = async () => {
  showSkeleton()
  filingStore.resetStore()
  await otherEntityStore.fetchOtherEntity(other_entity_id)
  if (filters.value.tab === 'direct') await filingStore.fetchFolder(otherEntity.value.filing_folder_root_id)
  await theFileSystem.value?.fetchAll()
  hideSkeleton()
}

watch(
  () => filters.value.tab,
  async (newTab) => {
    if (newTab) {
      await fetchAll()
    }
  },
)

onMounted(async () => {
  await Promise.all([fetchAll(), commitmentStore.fetchCommitments('funding-entity', other_entity_id)])
})
</script>

<template>
  <TheLayout>
    <OtherEntityHeader />
    <OtherEntityNav />
    <VButtonGroup class="mb-6">
      <VButton :active="filters.tab === 'shared'" class="w-32" @click="selectTab('shared')"> Shared Files </VButton>
      <VButton :active="filters.tab === 'direct'" class="w-32" @click="selectTab('direct')">Direct Files </VButton>
    </VButtonGroup>
    <div class="mx-auto">
      <TheFileSystem
        :key="filters.tab"
        ref="theFileSystem"
        :skeleton="skeleton"
        :rootFolder="filters.tab === 'direct' ? rootFolder : null"
        :shareholders="shareholders"
        :other_entity_id="filters.tab === 'shared' ? other_entity_id : null"
      />
    </div>
  </TheLayout>
</template>
